import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate, useLocation } from 'react-router-dom';

function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isRegistering, setIsRegistering] = useState(false);
  const [familyName, setFamilyName] = useState('');
  const [name, setName] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);

  const { login, register } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Check if we should start in registration mode
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const mode = urlParams.get('mode');
    if (mode === 'register' || location.state?.register) {
      setIsRegistering(true);
    }
  }, [location]);

  // Reset form when switching between login and registration
  const handleModeSwitch = () => {
    setIsRegistering(!isRegistering);
    setError('');
    setName('');
    setFamilyName('');
    setEmail('');
    setPassword('');
    setAcceptedTerms(false);
  };

  const validateForm = () => {
    if (!email || !password) {
      setError('Email and password are required');
      return false;
    }

    if (password.length < 6) {
      setError('Password must be at least 6 characters long');
      return false;
    }

    if (isRegistering) {
      if (!name || !familyName) {
        setError('Name and family name are required');
        return false;
      }

      if (name.trim().length < 2) {
        setError('Name must be at least 2 characters long');
        return false;
      }

      if (familyName.trim().length < 2) {
        setError('Family name must be at least 2 characters long');
        return false;
      }

      if (!acceptedTerms) {
        setError('You must accept the Terms and Conditions to create an account');
        return false;
      }
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      if (isRegistering) {
        await register({
          name: name.trim(),
          email: email.trim().toLowerCase(),
          password,
          familyName: familyName.trim()
        });

        // Redirect to check email page
        navigate('/check-email', {
          state: { email: email.trim().toLowerCase() }
        });
      } else {
        const response = await login(email.trim().toLowerCase(), password);
        console.log('Login successful, redirecting...', response);
        // Force a complete page reload with fresh state
        window.location.assign('/home');
      }
    } catch (err) {
      console.error('Login/Registration error:', err);
      
      // Handle different error formats
      let errorMessage = 'Authentication failed';
      
      if (err.response?.data?.body) {
        // Backend error response format
        try {
          const errorBody = JSON.parse(err.response.data.body);
          errorMessage = errorBody.error || errorMessage;
        } catch (parseError) {
          errorMessage = err.response.data.body || errorMessage;
        }
      } else if (err.response?.data?.error) {
        // Direct error format
        errorMessage = err.response.data.error;
      } else if (err.message) {
        // Standard error message
        errorMessage = err.message;
      } else if (typeof err === 'string') {
        // String error
        errorMessage = err;
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container">
      <div className="card" style={{ maxWidth: '400px', margin: '50px auto' }}>
        <h2 style={{ color: '#ff6b35', marginBottom: '24px', textAlign: 'center' }}>
          {isRegistering ? 'Create Account' : 'Welcome Back'}
        </h2>

        {error && (
          <div style={{
            color: '#ff4757',
            backgroundColor: 'rgba(255, 71, 87, 0.1)',
            padding: '12px',
            borderRadius: '8px',
            marginBottom: '20px',
            border: '1px solid rgba(255, 71, 87, 0.3)'
          }}>
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          {isRegistering && (
            <>
              <div className="form-group">
                <label>Full Name</label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Enter your full name"
                  required
                />
              </div>
              <div className="form-group">
                <label>Family Name</label>
                <input
                  type="text"
                  value={familyName}
                  onChange={(e) => setFamilyName(e.target.value)}
                  placeholder="Enter your family name"
                  required
                />
                <small style={{ color: '#b0b0b0', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                  This will be your family's budget group name
                </small>
              </div>
            </>
          )}

          <div className="form-group">
            <label>Email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Enter your email address"
              required
            />
          </div>

          <div className="form-group">
            <label>Password</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder={isRegistering ? "Create a password (min 6 characters)" : "Enter your password"}
              required
            />
            {isRegistering && (
              <small style={{ color: '#b0b0b0', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                Password must be at least 6 characters long
              </small>
            )}
          </div>

          {isRegistering && (
            <>
              <div style={{
                marginBottom: '20px',
                padding: '16px',
                background: 'rgba(255, 107, 53, 0.05)',
                border: '1px solid rgba(255, 107, 53, 0.2)',
                borderRadius: '8px'
              }}>
                <label style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '12px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  lineHeight: '1.4'
                }}>
                  <input
                    type="checkbox"
                    checked={acceptedTerms}
                    onChange={(e) => setAcceptedTerms(e.target.checked)}
                    style={{
                      marginTop: '2px',
                      accentColor: '#ff6b35'
                    }}
                    required
                  />
                  <span>
                    I have read and agree to the{' '}
                    <button
                      type="button"
                      onClick={() => navigate('/terms')}
                      style={{
                        background: 'none',
                        border: 'none',
                        color: '#ff6b35',
                        textDecoration: 'underline',
                        cursor: 'pointer',
                        fontWeight: '600',
                        padding: '0',
                        fontSize: 'inherit'
                      }}
                    >
                      Terms and Conditions
                    </button>
                  </span>
                </label>
              </div>

              <div style={{
                marginBottom: '20px',
                padding: '16px',
                background: 'rgba(52, 152, 219, 0.1)',
                border: '1px solid rgba(52, 152, 219, 0.3)',
                borderRadius: '8px',
                textAlign: 'center'
              }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  marginBottom: '12px'
                }}>
                  <span style={{ fontSize: '1.2rem' }}>🎉</span>
                  <h4 style={{
                    color: '#3498db',
                    margin: '0',
                    fontSize: '16px',
                    fontWeight: '600'
                  }}>
                    Start Your Free 30-Day Trial
                  </h4>
                </div>
                <p style={{
                  color: '#e0e0e0',
                  fontSize: '14px',
                  lineHeight: '1.4',
                  margin: '0 0 12px 0'
                }}>
                  Creating your account starts a <strong>free 30-day trial</strong> with full access to all features.
                  Our support team will reach out via email before your trial ends to help you choose the perfect plan.
                </p>
                <button
                  type="button"
                  onClick={() => window.open('/pricing', '_blank')}
                  style={{
                    background: 'rgba(52, 152, 219, 0.2)',
                    border: '1px solid #3498db',
                    color: '#3498db',
                    padding: '8px 16px',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '13px',
                    fontWeight: '500',
                    transition: 'all 0.2s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.background = 'rgba(52, 152, 219, 0.3)';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.background = 'rgba(52, 152, 219, 0.2)';
                  }}
                >
                  💰 View Pricing Plans
                </button>
              </div>
            </>
          )}

          <button type="submit" className="btn" disabled={loading} style={{ width: '100%' }}>
            {loading ? (
              <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                <span style={{
                  width: '16px',
                  height: '16px',
                  border: '2px solid transparent',
                  borderTop: '2px solid white',
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite'
                }}></span>
                {isRegistering ? 'Creating Account...' : 'Signing In...'}
              </span>
            ) : (
              'Sign In'
            )}
          </button>
        </form>

        {isRegistering && (
          <p style={{ textAlign: 'center', marginTop: '24px', color: '#b0b0b0' }}>
            If someone in your family already has a login, you'll need them to create one for you so you're able to help manage.
          </p>
        )}

        <p style={{ textAlign: 'center', marginTop: '24px', color: '#b0b0b0' }}>
          {isRegistering ? 'Already have an account?' : "Don't have an account?"}{' '}
          <button
            type="button"
            onClick={handleModeSwitch}
            style={{
              background: 'none',
              border: 'none',
              color: '#ff6b35',
              cursor: 'pointer',
              fontWeight: '600',
              textDecoration: 'underline'
            }}
          >
            {isRegistering ? 'Login' : 'Sign up'}
          </button>
        </p>
      </div>
    </div>
  );
}

export default Login;